import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import type { TooltipContentProps } from 'recharts'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { useHourlyStats } from '../api/admin-api'
import { BarChart3, Video, Zap } from 'lucide-react'

interface HourlyStatsModalProps {
  date: string | null
  onClose: () => void
}

function formatDateTitle(dateStr: string): string {
  const date = new Date(dateStr)
  const dayNames = ['일', '월', '화', '수', '목', '금', '토']
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일 (${dayNames[date.getDay()]})`
}

function formatNumber(num: number): string {
  return num.toLocaleString('ko-KR')
}

function formatHour(hour: number): string {
  return `${hour}시`
}

function formatHourTick(hour: number): string {
  return String(hour).padStart(2, '0')
}

function CustomTooltip({ active, payload, label }: Partial<TooltipContentProps<number, string>>) {
  if (!active || !payload?.length) return null
  const rawValue = payload[0]?.value
  const videoCount = typeof rawValue === 'number' ? rawValue : Number(rawValue)
  if (!Number.isFinite(videoCount)) return null

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="text-sm font-medium text-gray-900 mb-2">{formatHour(Number(label))}</p>
      <div className="space-y-1">
        <p className="text-sm text-emerald-600">
          비디오: <span className="font-semibold">{formatNumber(videoCount)}</span>건
        </p>
      </div>
    </div>
  )
}

export function HourlyStatsModal({ date, onClose }: HourlyStatsModalProps) {
  const { data, isLoading, error } = useHourlyStats(date)

  return (
    <Dialog open={!!date} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{date ? formatDateTitle(date) : ''} 시간별 통계</DialogTitle>
          <DialogDescription className="space-y-1">
            <span className="block">해당 날짜의 시간대별 비디오 생성 현황</span>
            {data?.timezone && (
              <span className="block text-xs text-gray-500">기준 시간대: {data.timezone}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="h-64 flex items-center justify-center text-gray-500">
            로딩 중...
          </div>
        ) : error ? (
          <div className="h-64 flex items-center justify-center text-red-500">
            통계를 불러올 수 없습니다: {error.message}
          </div>
        ) : data ? (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <BarChart3 className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs font-medium text-gray-500">일 합계</span>
                </div>
                <p className="text-lg font-bold text-gray-900">
                  {formatNumber(data.summary.totalVideos)}
                  <span className="text-sm font-medium text-gray-400 ml-0.5">건</span>
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Video className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs font-medium text-gray-500">생성 요청</span>
                </div>
                <p className="text-lg font-bold text-gray-900">
                  {formatNumber(data.summary.totalGenerations)}
                  <span className="text-sm font-medium text-gray-400 ml-0.5">회</span>
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs font-medium text-gray-500">피크 시간</span>
                </div>
                <p className="text-lg font-bold text-emerald-600">
                  {data.summary.peakHour.count > 0
                    ? `${data.summary.peakHour.hour}시`
                    : '-'}
                </p>
                {data.summary.peakHour.count > 0 && (
                  <p className="text-xs text-gray-400">{formatNumber(data.summary.peakHour.count)}건</p>
                )}
              </div>
            </div>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.hourlyStats} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis
                  dataKey="hour"
                  tickFormatter={(value) => formatHourTick(Number(value))}
                  tick={{ fontSize: 11, fill: '#64748B' }}
                  axisLine={{ stroke: '#E2E8F0' }}
                  tickLine={false}
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748B' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => formatNumber(value)}
                  width={45}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F1F5F9' }} />
                <Bar
                  dataKey="videoCount"
                  fill="#10B981"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={20}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
